import { useState } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Button, H1, HStack, Icon, Separator, Tag, Text, VStack } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { useNavigate } from 'react-router-dom';

import { AccountTaxationOptionsDocument } from './graphql/query.accountTaxationOptions.generated';

import { LegalAccountTaxationType } from '../graphql/schema';
import { WorkflowLayout } from '../Shared/layout/WorkflowLayout';
import { urlBuilder } from '../Shared/urlBuilder';

export const IraSelection = () => {
  const navigate = useNavigate();
  const [shouldShowUnavailable, setShouldShowUnavailable] = useState(false);

  const { data } = useGraphQLQuery(AccountTaxationOptionsDocument, {
    throwOnError: true,
    variables: { goalCreationOptionId: 'retirement' },
  });

  const goalCreationOption = data?.goalCreationOption;
  const options = goalCreationOption?.accountTaxationOptions;

  if (options == null || goalCreationOption == null) {
    return null;
  }

  const tcpByTaxationType: Partial<Record<LegalAccountTaxationType, boolean>> = {
    [LegalAccountTaxationType.RothIra]: goalCreationOption.rothIraTcp,
    [LegalAccountTaxationType.TraditionalIra]: goalCreationOption.traditionalIraTcp,
    [LegalAccountTaxationType.SepIra]: goalCreationOption.sepIraTcp,
  };

  const getHref = (taxationType: LegalAccountTaxationType): string => {
    const isTcpRecommended = tcpByTaxationType[taxationType] ?? false;
    return isTcpRecommended ? urlBuilder.appTcp.absolute() : '/rails/portfolio/selection';
  };

  const availableOptions = options.filter(o => o.isAvailable);
  const unavailableOptions = options.filter(o => !o.isAvailable);

  return (
    <WorkflowLayout
      workflowTitle={null}
      onBack={() => navigate(-1)}
      onClose={() => navigate('/')}
      pageTitle="IRA Selection"
    >
      <VStack css={{ rowGap: 'space.300' }}>
        <H1 variant="pageTitle">What type of IRA do you want?</H1>
        <VStack>
          <Separator />
          {availableOptions.map(option => (
            <SambaAutoLink
              css={{
                color: 'inherit',
                _hover: { color: 'inherit', textDecoration: 'none' },
                _active: { color: 'inherit', textDecoration: 'none' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                paddingTop: 'space.300',
                paddingBottom: 'space.300',
                borderBottom: '1px solid token(colors.border.weak)',
              }}
              to={getHref(option.taxationType)}
              key={option.id}
            >
              <VStack>
                <HStack css={{ columnGap: 'space.100' }}>
                  <Text css={{ textStyle: 'title100' }}>{option.name}</Text>
                  <Tag accent="grey" css={{ marginLeft: 'space.50' }}>
                    {option.taxationType === 'ROTH_IRA' ? 'Taxed now' : 'Taxed later'}
                  </Tag>
                </HStack>
                <Text as="p" variant="text50" css={{ color: 'text.weak' }}>
                  {option.description}
                </Text>
              </VStack>
              <Icon name="CaretRight" size="small" css={{ marginLeft: 'space.100' }} />
            </SambaAutoLink>
          ))}
        </VStack>

        {unavailableOptions.length > 0 && (
          <>
            <Button
              id="unavailable-ira-options-trigger"
              variant="textSecondary"
              aria-expanded={!shouldShowUnavailable}
              aria-controls="unavailable-ira-options"
              onClick={() => setShouldShowUnavailable(v => !shouldShowUnavailable)}
              css={{
                color: 'text.weak',
              }}
            >
              {!shouldShowUnavailable ? 'Hide' : 'Show'} unavailable ({unavailableOptions.length})
            </Button>
            <Box
              id="unavailable-ira-options"
              hidden={!shouldShowUnavailable}
              role="region"
              aria-labelledby="unavailable-ira-options-trigger"
              css={{ display: 'flex', flexDirection: 'column', rowGap: 'space.300', '&[hidden]': { display: 'none' } }}
            >
              <VStack css={{ rowGap: 'space.100' }}>
                {unavailableOptions.map(option => (
                  <HStack
                    key={option.id}
                    css={{
                      alignItems: 'center',
                      columnGap: 'space.150',
                    }}
                  >
                    <Icon name="BeachUmbrella" size="medium" css={{ flexShrink: 0 }} />
                    <Text css={{ textStyle: 'title100' }}>{option.name}</Text>
                  </HStack>
                ))}
              </VStack>
              <Text as="p" css={{ textStyle: 'title100' }}>
                Why can&#8217;t I open certain types of IRAs?
              </Text>
              <Text as="p" variant="text50" css={{ color: 'text.weak' }}>
                We only let you open one of each type. We limit users to one IRA of each type.
              </Text>
            </Box>
          </>
        )}
      </VStack>
    </WorkflowLayout>
  );
};
